interface IQueryStringifyOptions {
    skipEmptyString?: boolean;
    skipNull?: boolean;
}

/**
 * Retrieves the value of a parameter in an url.
 * @param url The url that contains the parameter.
 * @param paramName The parameter name to get the value for.
 * @returns The value of the parameter or null if nothing is found or the url/query is invalid.
 */
export function getParameterValueFromUrl(url: string, paramName: string): string | null {
    let urlObject: URL;
    try {
        urlObject = new URL(url);
    } catch {
        return null;
    }

    return urlObject.searchParams.get(paramName) || null;
}

/**
 * Parses the query string into an object. Leading ? or # are ignored, so you can pass location.search or location.hash directly.
 * @param query the query search or hash string (f.e: '?bar=foo', '#bar=foo').
 * @returns Object of sorted keys with values for the query (f.e: { bar: foo }).
 */
export function parseQueryString(query?: string): Record<string, string> {
    if (!query) {
        return {};
    }

    const queryParams = query.startsWith('#') ? query.substring(1) : query;

    const urlSearchParams = new URLSearchParams(queryParams);
    urlSearchParams.sort();

    return Object.fromEntries(urlSearchParams);
}

/**
 * Stringify an object into a query string of sorted keys with values.
 * @param object the object to convert into a string (f.e: { bar: foo }).
 * @param options the options that can change the output of the query string.
 * @returns a query string without a leading ?, # or undefined values (f.e: 'bar=foo').
 */
export function stringify(
    object?: Record<string, string | number | boolean | null | undefined>,
    options?: IQueryStringifyOptions
): string {
    if (!object) {
        return '';
    }

    const stringifiedObject: Record<string, string> = {};
    for (const [key, value] of Object.entries(object)) {
        if (typeof value === 'undefined') {
            continue;
        }
        if (options?.skipNull && value === null) {
            continue;
        }
        if (options?.skipEmptyString && value === '') {
            continue;
        }

        stringifiedObject[key] = String(value);
    }

    const urlSearchParams = new URLSearchParams(stringifiedObject);
    urlSearchParams.sort();

    return urlSearchParams.toString();
}
